import {Component, OnInit} from '@angular/core';
import {DeploymentsService} from "../deployments.service";
import {DataService} from "../data.service";
import {ActivatedRoute, Router} from "@angular/router";
import * as moment from 'moment';
import {TimezoneService} from "../timezone.service";
import {NgxSpinnerService} from "ngx-spinner";
import * as Highcharts from 'highcharts';
import {parenthesizedExpression} from "@babel/types";

declare var require: any;
let Boost = require('highcharts/modules/boost');
let noData = require('highcharts/modules/no-data-to-display');
let More = require('highcharts/highcharts-more');

Boost(Highcharts);
noData(Highcharts);
More(Highcharts);

@Component({
    selector: 'app-data-plot-interactive',
    templateUrl: './data-plot-interactive.component.html',
    styleUrls: ['./data-plot-interactive.component.scss']
})
export class DataPlotInteractiveComponent implements OnInit {
    data: any = null;
    plotlyGraph: any = null;

    highcharts = Highcharts;
    highchartsGraphOptions: any = null;

    zingChartConfig: any = null;
    graphLoadStart: number = 1;

    dataset: string;
    graphVariable: string = 'sci_water_depth';
    colorVariable: string | null;
    variableIndex: number;
    availableVariables: string[] = [];

    libraries: string[] = ['plotly.js', 'highcharts', /*'zingChart'*/];
    selectedLibrary: string | null = null;

    deploymentNumber: number | null;

    missions: any = null;
    erddapMetadata: any = null;
    selectedMetadata: { delayed: { id: string, variables: string[] } | null, realtime: { id: string, variables: string[] } | null };
    variables: string[] = ['temperature', 'salinity', 'density'];
    selectedVariables: { [key: string]: boolean } = {};
    missionHasData: any = null;
    selectedMission: any = null;
    selectedDatasetType: 'Delayed' | 'Realtime' | null;

    moment: any = moment;
    timezone: any = null;
    missionStart: any = null;
    missionEnd: any = null;

    graphStartTime: any = null;
    graphEndTime: any = null;
    validRange: boolean | null = null;

    constructor(
        private deploymentsService: DeploymentsService,
        private dataService: DataService,
        private route: ActivatedRoute,
        private router: Router,
        private spinner: NgxSpinnerService,
        private timezoneService: TimezoneService) {
        this.timezone = timezoneService.timezone;
    }

    ngOnInit() {
        this.deploymentsService.getAllDeployments().then(response => {
            this.missions = response.sort((a, b) => b.deployment_number - a.deployment_number);
            this.deploymentsService.getMetadata().then(response => {
                this.erddapMetadata = response;
                this.loadMission();
            });
        });

        this.deploymentNumber = parseInt(this.route.snapshot.params['deploymentNumber']);
    }

    loadMission() {
        this.colorVariable = null;
        this.selectedDatasetType = null;
        this.selectedLibrary = null;

        if (!this.deploymentNumber) {
            this.selectedMission = null;
            return;
        }

        this.selectedMission = Array.getByProperty(this.missions, this.deploymentNumber, 'deployment_number');

        if (this.selectedMission) {
            this.selectedMetadata = this.erddapMetadata[this.deploymentNumber];
            this.missionHasData = this.selectedMetadata.delayed || this.selectedMetadata.realtime;
            // if realtime is available, select it, otherwise select delayed
            if (this.selectedMetadata.realtime) {
                this.selectedDatasetType = 'Realtime';
            } else if (this.selectedMetadata.delayed) {
                this.selectedDatasetType = 'Delayed';
            }
            this.updateAvailableVariables();

            this.missionStart = moment(this.selectedMission.start_time).format('YYYY-MM-DD');
            if (this.selectedMission.end_time) {
                this.missionEnd = moment(this.selectedMission.end_time).format('YYYY-MM-DD');
            } else {
                this.missionEnd = moment().format('YYYY-MM-DD');
            }

            //skip selecting dates
            this.graphStartTime = this.missionStart;
            this.graphEndTime = this.missionEnd;
            this.validRange = true;
        }
    }

    updateAvailableVariables() {
        if (this.selectedDatasetType == 'Realtime' && this.selectedMetadata.realtime) {
            this.variables = this.selectedMetadata.realtime.variables.filter(variable => this.variables.includes(variable));
        } else if (this.selectedDatasetType == 'Delayed' && this.selectedMetadata.delayed) {
            this.variables = this.selectedMetadata.delayed.variables.filter(variable => this.variables.includes(variable));
        }
    }

    checkDateRange(){
        if (this.graphStartTime && this.graphEndTime) {
            this.data = null;
            if (this.graphStartTime >= this.missionStart && this.graphEndTime <= this.missionEnd && this.graphStartTime <= this.graphEndTime) {
                this.validRange = true;
            } else {
                this.validRange = false;
            }
        }
    }

    getData() {
        this.resetPlots();

        if (this.selectedDatasetType && this.graphVariable && this.validRange) {
            if (this.selectedDatasetType == 'Delayed' && this.selectedMetadata.delayed) {
                this.dataset = this.selectedMetadata.delayed.id;
            } else if (this.selectedDatasetType == 'Realtime' && this.selectedMetadata.realtime) {
                this.dataset = this.selectedMetadata.realtime.id;
            } else {
                throw ReferenceError('Selected dataset type is unavailable');
            }

            let selected = Object.keys(this.selectedVariables).filter(variable => this.selectedVariables[variable]);
            if (!selected.length) {
                throw ReferenceError('No variables selected');
            }

            let endTime: any = moment(this.graphEndTime).add({hour: 23, minute: 59, second: 59});

            //re-format for dataService
            endTime = moment(endTime).format('YYYY-MM-DD HH:mm:ss');
            let startTime = moment(this.graphStartTime).format('YYYY-MM-DD HH:mm:ss');

            this.dataService.getErddapDataset(this.dataset, this.graphVariable, selected, startTime, endTime).then(response => {
                this.data = response['table'];
                this.availableVariables = this.data['columnNames'].slice(2);
            });
        }
    }

    loadGraph() {
        this.resetPlots();

        if (this.selectedLibrary && this.colorVariable) {
            this.variableIndex = this.data['columnNames'].indexOf(this.colorVariable);
            this.graphLoadStart = performance.now();
            if (this.selectedLibrary == 'plotly.js') {
                this.loadPlotlyGraph();
            } else if (this.selectedLibrary == 'highcharts') {
                this.loadHighchartsGraph();
            } else {
                this.loadZingChartGraph();
            }
        }
    }

    resetPlots() {
        this.plotlyGraph = null;
        this.highchartsGraphOptions = null;
        this.zingChartConfig = null;
    }

    afterPlotLoaded() {
    }

    loadPlotlyGraph() {
        let vm = this;
        let xData: string[] = [];
        let yData: string[] = [];
        let color: string[] = [];

        this.data['rows'].forEach(function(dataPoint) {
            let date = moment(dataPoint[0]).format('YYYY-MM-DD HH:mm:ss');
            xData.push(date);
            yData.push(dataPoint[1]);
            color.push(dataPoint[vm.variableIndex]);
        });

        this.plotlyGraph = {
            data: [{
                x: xData,
                y: yData,
                type: 'scatter',
                mode: 'markers',
                marker: {
                    color: color,
                    colorscale: 'Jet',
                    showscale: true,
                    colorbar: {
                        title: {text: this.colorVariable, side: 'right'},
                    }
                },
                hovertemplate: `date: %{x} <br>depth: %{y:.2f} <br>${this.colorVariable}: %{marker.color:.2f}<extra></extra>`
            }],
            layout: {
                title: this.dataset,
                height: 500,
                width: 400,
                xaxis: {title: 'date'},
                yaxis: {title: this.graphVariable, autorange: 'reversed'},
                hovermode: 'closest'
            },
            config: {}
        };
    }

    loadHighchartsGraph() {
        let vm = this;
        let seriesData: any = [];
        let colorData: any = [];

        this.data['rows'].forEach(function(dataPoint) {
            seriesData.push([new Date(dataPoint[0]), dataPoint[1]]);
            colorData.push(dataPoint[vm.variableIndex]);
        });

        this.highchartsGraphOptions = {
            chart: {
                height: 500,
                width: 400,
                zoomType: 'xy',
                panning: true,
                panKey: 'shift',
                events: {
                    render: this.afterPlotLoaded()
                }
            },
            title: {
                text: this.dataset
            },
            subtitle: {
                text: 'Click and drag to zoom in. Hold down shift key to pan.'
            },
            legend: {
                enabled: false
            },
            tooltip: {
                formatter: function() {
                    return 'date: ' + moment(this.x).format('YYYY-MM-DD HH:mm:ss')
                        + '<br>depth: ' + this.y
                        + '<br>' + vm.colorVariable + ': ' + colorData[this.point.index];
                }
            },
            xAxis: {
                type: 'datetime'
            },
            yAxis: {
                title: {
                    text: this.graphVariable,
                    enabled: true
                },
                reversed: true
            },
            series: [{
                type: 'scatter',
                data: seriesData
            }],
        };
    }

    loadZingChartGraph() {
        let vm = this;
        let seriesData: any = [];
        let colorData: any = [];

        this.data['rows'].forEach(function(dataPoint) {
            seriesData.push([Date.parse(dataPoint[0]), dataPoint[1]]);
            colorData.push(dataPoint[vm.variableIndex]);
        });

        this.zingChartConfig = {
            type: 'scatter',
            title: {
                text: this.dataset
            },
            scaleX: {
                label: { text: 'Date' },
                zooming: true,
                transform: { type: 'date' }
            },
            scaleY: {
                mirrored: true,
                zooming: true
            },
            plot: {
                tooltip: {
                    text: 'date: %kt<br>depth: %vt',
                    transform: {
                        type: 'date',
                        all: '%Y-%mm-%dd %H:%i:%s'
                    }
                }
            },
            series: [{
                values: seriesData,
                marker: {
                    'border-width': 0
                }
            }]
        };
    }
}